/// <amd-module name="VSSUI/Components/PillGroup/PillGroup" />
import * as React from "react";
import { Button } from "VSSUI/Button";
import { IconSize } from "VSSUI/Icon";
import { IIntersectionContext, Intersection, IntersectionContext } from "VSSUI/Intersection";
import { Pill, PillSize } from "VSSUI/Pill";
import * as Resources from "VSSUI/Resources.Widgets";
import { css } from "VSSUI/Util";
import { IPillGroupProps, PillGroupOverflow } from "./PillGroup.Props";

interface IPillGroupState {
    overflowing: boolean;
    overflow?: PillGroupOverflow;
}

export class PillGroup extends React.Component<IPillGroupProps, IPillGroupState> {
    private intersectionContext: IIntersectionContext;
    private observing = false;

    public constructor(props: IPillGroupProps) {
        super(props);
        this.state = { overflowing: false, overflow: this.props.overflow };
    }

    public render() {
        const { onMouseEnter, onMouseLeave } = this.props;
        const { overflow } = this.state;
        let contents, extend: JSX.Element;
        extend = (
            <Pill size={PillSize.compact}>
                <Button
                    ariaLabel={this.state.overflow === this.props.overflow ? Resources.ShowMore : Resources.ShowLess}
                    className="bolt-pill-button"
                    iconProps={{ iconName: "More", size: IconSize.inherit }}
                    onClick={this.onClick}
                    subtle={true}
                    tooltipProps={{ text: this.state.overflow === this.props.overflow ? Resources.ShowMore : Resources.ShowLess }}
                />
            </Pill>
        );
        // If we have fade enabled put the intersection observer and the overflow element in place.
        if (overflow === PillGroupOverflow.fade) {
            contents = (
                <IntersectionContext.Consumer>
                    {intersectionContext => {
                        this.intersectionContext = intersectionContext;
                        return (
                            <div className="bolt-pill-overflow flex-row">
                                <div className="bolt-pill-group-inner flex-row">
                                    {this.state.overflowing && extend}
                                    {this.props.children}
                                </div>
                                <div className="bolt-pill-observe" ref={element => this.observeElement(element)} />
                            </div>
                        );
                    }}
                </IntersectionContext.Consumer>
            );
        } else {
            contents = (
                <div className="bolt-pill-group-inner flex-row">
                    {this.state.overflowing && extend}
                    {this.props.children}
                </div>
            );
        }

        // Wrap the pill group in the outer shell.
        contents = (
            <div
                className={css(
                    this.props.className,
                    "bolt-pill-group flex-row",
                    overflow === PillGroupOverflow.wrap
                        ? "overflow-wrap"
                        : overflow === PillGroupOverflow.fade
                        ? this.state.overflowing && "overflow-fade"
                        : undefined
                )}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {contents}
            </div>
        );

        // If we have fade enabled wrap the PillGroup in the intersection.
        if (overflow === PillGroupOverflow.fade) {
            contents = <Intersection>{contents}</Intersection>;
        }

        return contents;
    }

    public componentDidMount() {
        if (this.intersectionContext) {
            this.intersectionContext.register(this.onIntersect);
        }
    }

    public componentWillUnmount() {
        if (this.intersectionContext) {
            this.intersectionContext.unregister(this.onIntersect);
        }
    }

    private onClick = (event: React.MouseEvent<HTMLDivElement>) => {
        this.setState({
            overflow: this.state.overflow === this.props.overflow ? PillGroupOverflow.wrap : this.props.overflow
        });
        event?.preventDefault();
    };

    private observeElement = (element: HTMLElement | null) => {
        if (element && this.intersectionContext && !this.observing) {
            this.intersectionContext.observe(element);
            this.observing = true;
        }
    };

    private onIntersect = (entries: IntersectionObserverEntry[]) => {
        this.setState({
            // Only want the effect if the right side is cut off
            overflowing: Math.round(entries[0].rootBounds!.right) < Math.round(entries[0].boundingClientRect.right)
        });
    };
}
